import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Grid, Accordion, AccordionSummary, AccordionDetails, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Link } from 'react-router-dom';
import logo from '../assets/logo.png';
import { C, F, G, SH } from '../styles/tokens';

const SERVICES = [
  {
    title: 'Trademark Search',
    desc: 'Comprehensive search on the IP India trademark registry to check availability of your brand name, logo, or slogan before filing.',
  },
  {
    title: 'Trademark Registration',
    desc: 'End-to-end filing of trademark application under the Trade Marks Act 1999. We select the right class, draft the application, and file on the IP India portal.',
  },
  {
    title: 'Trademark Renewal',
    desc: 'Trademark registration is valid for 10 years. We handle timely renewal so your brand protection never lapses.',
  },
  {
    title: 'Trademark Objection Reply',
    desc: 'If the trademark examiner raises an objection, we draft and file a strong legal reply with supporting arguments and evidence.',
  },
  {
    title: 'Trademark Assignment',
    desc: 'Transfer ownership of a registered trademark to another individual or company via a properly executed assignment deed.',
  },
  {
    title: 'Copyright Registration',
    desc: 'Protect original creative works — software, websites, content, music, and art — with copyright registration under the Copyright Act 1957.',
  },
];

const STEPS = [
  { step: '01', title: 'Brand Consultation', desc: 'Discuss your brand name, logo, and business category to determine the correct trademark class under the Nice Classification.' },
  { step: '02', title: 'Trademark Search', desc: 'We search the IP India database for identical or similar marks that could conflict with your application.' },
  { step: '03', title: 'Application Drafting', desc: 'Prepare Form TM-A with accurate applicant details, brand description, goods/services list, and class selection.' },
  { step: '04', title: 'Filing on IP India Portal', desc: 'Application filed online on the IP India portal. TM Application Number allotted immediately on filing.' },
  { step: '05', title: 'Examination & Objection', desc: 'Trademark office examines the application within 12–18 months. We respond to any objection within the prescribed time.' },
  { step: '06', title: 'Registration Certificate', desc: 'Once accepted and published in the Trademark Journal without opposition, the registration certificate is issued.' },
];

const FAQS = [
  {
    q: 'Why is trademark registration important for a business?',
    a: 'Trademark registration gives you exclusive rights to use your brand name, logo, or slogan in India. It prevents competitors from copying your brand, provides legal recourse against infringers, and adds valuable intellectual property to your business — which can be licensed or sold.',
  },
  {
    q: 'How long does trademark registration take in India?',
    a: 'The trademark application number is allotted immediately on filing. The examination report is typically issued within 12–18 months. If no objection or opposition arises, registration is completed within 18–24 months. You can use the ™ symbol immediately after filing and ® only after registration.',
  },
  {
    q: 'What is a trademark class, and how do I choose the right one?',
    a: 'Trademarks in India are classified into 45 classes under the Nice Classification — Classes 1–34 for goods and Classes 35–45 for services. You must file under the class that covers your products or services. Pracco helps you identify the correct class(es) before filing.',
  },
  {
    q: 'Can I register a trademark for my startup or personal name?',
    a: 'Yes. Individuals, startups, LLPs, companies, and trusts can all apply for trademark registration. If you have a small business or are a solopreneur, you can register your brand in your personal name as a proprietor.',
  },
  {
    q: 'What happens if my trademark application is objected to?',
    a: 'If the trademark examiner raises an objection (under absolute or relative grounds), we prepare a detailed reply with legal arguments and supporting evidence. If the reply is rejected, we can attend a hearing before the trademark office on your behalf.',
  },
  {
    q: 'How is trademark different from copyright and patent?',
    a: 'Trademark protects brand names, logos, and slogans. Copyright protects original creative works (writing, music, art, software). Patent protects inventions and innovations. All three are forms of intellectual property (IP) but serve different purposes and have different registration processes.',
  },
];

const TrademarkPage = () => (
  <>
    <Helmet>
      <title>Trademark Registration in India — Brand Name & Logo Protection | Pracco</title>
      <meta name="description" content="Register your trademark in India with Pracco. Brand name & logo protection, trademark search, objection reply & renewal. Expert IP lawyers. Fast filing. Free consultation — +91-9311817126." />
      <meta name="keywords" content="trademark registration India, brand name registration India, logo trademark registration, trademark search India, trademark filing online, IP India trademark, trademark objection reply, trademark renewal, copyright registration India" />
      <link rel="canonical" href="https://pracco.in/trademark-registration" />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://pracco.in/" },
          { "@type": "ListItem", "position": 2, "name": "Trademark Registration", "item": "https://pracco.in/trademark-registration" },
        ],
      })}</script>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to Register a Trademark in India",
        "description": "Step-by-step process to file and obtain trademark registration in India.",
        "totalTime": "P540D",
        "step": STEPS.map((s) => ({
          "@type": "HowToStep",
          "position": parseInt(s.step),
          "name": s.title,
          "text": s.desc,
        })),
      })}</script>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": FAQS.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      })}</script>
    </Helmet>

    {/* ── Mini Header ── */}
    <Box component="header" sx={{ px: { xs: 2, md: 6 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${C.border}`, background: C.white }}>
      <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
        <Box component="img" src={logo} alt="Pracco" sx={{ height: 36 }} />
      </Link>
      <Button
        component="a"
        href="https://wa.me/9311817126"
        target="_blank"
        rel="noopener noreferrer"
        startIcon={<WhatsAppIcon />}
        sx={{ background: C.whatsapp, color: C.white, fontFamily: F.body, fontWeight: 600, fontSize: 13, px: 2.5, py: 1, borderRadius: 2, textTransform: 'none', '&:hover': { background: C.whatsappDk } }}
      >
        Free Consultation
      </Button>
    </Box>

    {/* ── Hero ── */}
    <Box component="section" sx={{ background: 'linear-gradient(160deg, #fdf4ff 0%, #F8FAFC 55%, #f3e8ff 100%)', px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 9 }, textAlign: 'center' }}>
      <Typography component="h1" sx={{ fontFamily: F.heading, fontSize: { xs: 28, md: 44 }, fontWeight: 800, color: C.text, lineHeight: 1.15, mb: 2 }}>
        Trademark Registration in India
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: { xs: 15, md: 18 }, color: C.textMid, maxWidth: 680, mx: 'auto', lineHeight: 1.7, mb: 4 }}>
        Protect your brand name, logo, and slogan legally. Expert trademark search, filing, and objection reply.
        Use ™ immediately after filing. Free consultation.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          component="a"
          href="https://wa.me/9311817126"
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<WhatsAppIcon />}
          sx={{ background: '#7c3aed', color: C.white, fontFamily: F.body, fontWeight: 700, fontSize: 15, px: 4, py: 1.5, borderRadius: 2.5, textTransform: 'none', boxShadow: '0 6px 20px rgba(124,58,237,0.35)', '&:hover': { background: '#6d28d9' } }}
        >
          Register My Trademark
        </Button>
        <Button
          component={Link}
          to="/"
          variant="outlined"
          sx={{ fontFamily: F.body, fontWeight: 600, fontSize: 14, px: 3, py: 1.5, borderRadius: 2.5, textTransform: 'none', borderColor: '#7c3aed', color: '#7c3aed', '&:hover': { background: '#fdf4ff' } }}
        >
          View All Services
        </Button>
      </Box>
    </Box>

    {/* ── Service Types ── */}
    <Box component="section" sx={{ px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 8 }, background: C.white }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.text, mb: 1, textAlign: 'center' }}>
        Trademark &amp; IP Services
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: 15, color: C.textMid, textAlign: 'center', mb: 5, maxWidth: 560, mx: 'auto' }}>
        End-to-end intellectual property protection — from search to registration and beyond.
      </Typography>
      <Grid container spacing={3} sx={{ maxWidth: 1100, mx: 'auto' }}>
        {SERVICES.map((s) => (
          <Grid item xs={12} sm={6} md={4} key={s.title}>
            <Box sx={{ border: `1px solid ${C.border}`, borderRadius: 3, p: 3, height: '100%', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: SH.cardHover } }}>
              <CheckCircleOutlineIcon sx={{ color: '#7c3aed', mb: 1 }} />
              <Typography sx={{ fontFamily: F.heading, fontSize: 16, fontWeight: 700, color: C.text, mb: 1 }}>{s.title}</Typography>
              <Typography sx={{ fontFamily: F.body, fontSize: 14, color: C.textMid, lineHeight: 1.7 }}>{s.desc}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>

    {/* ── Process Steps ── */}
    <Box component="section" sx={{ px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 8 }, background: C.bg }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.text, mb: 1, textAlign: 'center' }}>
        Trademark Registration Process
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: 15, color: C.textMid, textAlign: 'center', mb: 5, maxWidth: 560, mx: 'auto' }}>
        From brand search to registration certificate — we handle every step.
      </Typography>
      <Grid container spacing={3} sx={{ maxWidth: 1000, mx: 'auto' }}>
        {STEPS.map((s) => (
          <Grid item xs={12} sm={6} md={4} key={s.step}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Typography sx={{ fontFamily: F.heading, fontSize: 34, fontWeight: 800, color: C.border, lineHeight: 1, minWidth: 44 }}>{s.step}</Typography>
              <Box>
                <Typography sx={{ fontFamily: F.heading, fontSize: 15, fontWeight: 700, color: C.text, mb: 0.5 }}>{s.title}</Typography>
                <Typography sx={{ fontFamily: F.body, fontSize: 13, color: C.textMid, lineHeight: 1.7 }}>{s.desc}</Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>

    {/* ── FAQ ── */}
    <Box component="section" sx={{ px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 8 }, background: C.white, maxWidth: 860, mx: 'auto' }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.text, mb: 4, textAlign: 'center' }}>
        Frequently Asked Questions — Trademark Registration
      </Typography>
      {FAQS.map((faq) => (
        <Accordion key={faq.q} disableGutters elevation={0} sx={{ border: `1px solid ${C.border}`, mb: 1.5, borderRadius: '10px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#7c3aed' }} />}>
            <Typography sx={{ fontFamily: F.body, fontWeight: 600, fontSize: 15, color: C.text }}>{faq.q}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ fontFamily: F.body, fontSize: 14, color: C.textMid, lineHeight: 1.8 }}>{faq.a}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>

    {/* ── CTA ── */}
    <Box component="section" sx={{ background: 'linear-gradient(135deg, #4c1d95 0%, #5b21b6 100%)', px: { xs: 2.5, md: 8 }, py: { xs: 6, md: 7 }, textAlign: 'center' }}>
      <Typography component="h2" sx={{ fontFamily: F.heading, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: C.white, mb: 1.5 }}>
        Protect Your Brand Today
      </Typography>
      <Typography sx={{ fontFamily: F.body, fontSize: 15, color: 'rgba(255,255,255,0.7)', mb: 4, maxWidth: 480, mx: 'auto' }}>
        Don't let someone else register your brand name. File your trademark application today with Pracco.
      </Typography>
      <Button
        component="a"
        href="https://wa.me/9311817126"
        target="_blank"
        rel="noopener noreferrer"
        startIcon={<WhatsAppIcon />}
        sx={{ background: C.whatsapp, color: C.white, fontFamily: F.body, fontWeight: 700, fontSize: 16, px: 5, py: 1.75, borderRadius: 2.5, textTransform: 'none', boxShadow: SH.btnGreen, '&:hover': { background: C.whatsappDk, boxShadow: SH.btnGreenHover } }}
      >
        WhatsApp Us Now
      </Button>
    </Box>

    {/* ── Mini Footer ── */}
    <Box component="footer" sx={{ px: { xs: 2.5, md: 8 }, py: 3, background: C.text, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
      <Typography sx={{ fontFamily: F.body, fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
        © {new Date().getFullYear()} Pracco Corporate Services Pvt. Ltd. — info@pracco.in · +91-9311817126
      </Typography>
      <Link to="/" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>
        ← Back to Home
      </Link>
    </Box>
  </>
);

export default TrademarkPage;
